import { isPlainObject, PlainObject } from '../helpers/helpers';

export interface UnresolvedReference {
  uid: string;
  _content_type_uid: string;
}

export const isUnresolvedReference = (
  value: unknown
): value is UnresolvedReference =>
  isPlainObject(value) &&
  typeof value.uid === 'string' &&
  typeof value._content_type_uid === 'string' &&
  Object.keys(value).length <= 2;

type ReferenceVisitor = (
  reference: UnresolvedReference,
  parentEntryUids: Set<string>,
  replaceWith: (entry: PlainObject) => void
) => void;

export const visitUnresolvedReferences = (
  rootEntry: PlainObject,
  visitor: ReferenceVisitor
): void => {
  const visited = new WeakSet<object>();

  const visit = (
    node: unknown,
    isRootEntry: boolean,
    parentEntryUids: Set<string>
  ): void => {
    if (!node || typeof node !== 'object' || visited.has(node)) return;

    visited.add(node);

    if (Array.isArray(node)) {
      node.forEach((item, index) => {
        if (isUnresolvedReference(item)) {
          visitor(item, parentEntryUids, (entry) => {
            node[index] = entry;
          });

          return;
        }

        visit(item, false, parentEntryUids);
      });

      return;
    }

    const fields = node as PlainObject;
    const uidsIncludingThisEntry =
      typeof fields.uid === 'string'
        ? new Set([...parentEntryUids, fields.uid])
        : parentEntryUids;

    Object.entries(fields).forEach(([fieldName, value]) => {
      if (!isRootEntry && fieldName === 'body') return;

      if (isUnresolvedReference(value)) {
        visitor(value, uidsIncludingThisEntry, (entry) => {
          fields[fieldName] = entry;
        });

        return;
      }

      visit(value, false, uidsIncludingThisEntry);
    });
  };

  visit(rootEntry, true, new Set());
};
